import { useState } from 'react'

import { useCreation, useRequest, useUpdateEffect } from 'ahooks'
import { UploadFile } from 'antd'

import { OssFileDetail } from '@eastcoal/api/nexus/interface'
import { queryFileDetailBatchApi } from '@eastcoal/api/nexus/oss/queryFileDetailBatch'

import { DEFAULT_STALE_TIME } from '../../../../config/useRequest'
import { isEmpty, omit } from '../../../../utils/object'

import { UploadItem, UseUploadResultProps } from './interface'
import { transformOssDetail } from './transformOssDetail'
import { tarnsformUploadFile } from './transformUploadFile'
import { isFailed, isSuccessful, isUploading } from './uploadStatus'

/**
 * 把 ossId 转换成 OssFileDetail
 */
export const useUploadResult = ({
  thumbnail,
  thumbnailStyle,
  value,
  onChange,
  onDetail
}: UseUploadResultProps) => {
  const [result, setResult] = useState<Record<string, UploadItem>>({})

  const newOssIds = useCreation(() => {
    const items = Object.values(result)
    if (!value) {
      return []
    }
    return value.filter(
      id => !isEmpty(id) && items.every(item => item.ossId !== id)
    )
  }, [value])

  useUpdateEffect(() => {
    const resultValue = Object.values(result)
    if (!value?.length) {
      if (resultValue.length) {
        setResult({})
      }
      return
    }
    let isChange = false
    const newResult = { ...result }
    resultValue.forEach(({ ossId, uid }) => {
      if (ossId && !value.includes(ossId)) {
        delete newResult[uid]
        isChange = true
      }
    })
    if (isChange) {
      setResult(newResult)
    }
  }, [value])

  const { error, loading, refreshAsync } = useRequest(
    () =>
      queryFileDetailBatchApi(
        {
          thumbnail,
          thumbnailStyle,
          ossIds: newOssIds
        },
        {
          silence: true
        }
      ),
    {
      manual: false,
      ready: newOssIds.length > 0,
      refreshDeps: [newOssIds],
      staleTime: DEFAULT_STALE_TIME,
      onSuccess(res) {
        const newState = res.reduce(
          (acc, cur) => {
            const item = transformOssDetail(cur)
            acc[item.uid] = item
            return acc
          },
          {} as Record<string, UploadItem>
        )
        setResult(state => {
          const mergedState = {
            ...state,
            ...newState
          }
          const successfulItems = Object.values(mergedState).filter(item =>
            isSuccessful(item.status)
          )
          onDetail?.(successfulItems)
          return mergedState
        })
      }
    }
  )

  const triggerChange = (state: Record<string, UploadItem>) => {
    const items = Object.values(state)
    const successfulItems = items.filter(item => isSuccessful(item.status))
    const successfulKeys = successfulItems.map(item => `${item.ossId}`)
    onChange?.(successfulKeys, successfulItems)
  }

  const appendItem = (item: UploadItem) => {
    setResult(state => {
      const newState = {
        ...state,
        [item.uid]: item
      }
      // 收到新上传成功的文件，触发 onChange
      if (isSuccessful(item.status)) {
        triggerChange(newState)
      }
      onDetail?.(Object.values(newState))
      return newState
    })
  }

  const replaceItem = (item: UploadItem) => {
    setResult(() => {
      const newState = {
        [item.uid]: item
      }
      // 上传结束之后，触发 onChange
      if (!isUploading(item.status)) {
        triggerChange(newState)
      }
      onDetail?.([item])
      return newState
    })
  }

  const removeItem = (uid: string) => {
    setResult(state => {
      const newState = omit(state, [uid])
      triggerChange(newState)
      onDetail?.(Object.values(newState))
      return newState
    })
  }

  const receiveFile = ({
    file,
    replaced
  }: {
    file: UploadFile<OssFileDetail>
    replaced: boolean
  }) => {
    const { uid, status, response = {} } = file
    const updateItem = replaced ? replaceItem : appendItem
    if (isUploading(status)) {
      updateItem(tarnsformUploadFile(file))
    } else if (isSuccessful(status)) {
      updateItem({
        ...transformOssDetail(response),
        uid
      })
    } else if (isFailed(status)) {
      updateItem(tarnsformUploadFile(file))
    } else {
      removeItem(uid)
    }
  }

  return {
    error,
    loading,
    reload: refreshAsync,
    result,
    receiveFile
  }
}
